import React, {useRef, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {useParams} from 'react-router';
import { useNavigate } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import {DndProvider} from 'react-dnd-multi-backend';
import {HTML5toTouch} from 'rdndmb-html5-to-touch';
import apiHelper from 'helpers/api-helper';
import {getText} from 'helpers/language-helper';
import {getBrowserLanguage} from 'helpers/language-helper';
import Loading from 'components/loading/loading';
import Login from './login';
import FacilitatorController from 'components/users/facilitator-controller';
import GroupController from 'components/users/group-controller';
import { loginUiTexts } from 'data/ui-texts';

const Auth = ({isFullscreen, gameType, companies, handleToggleFullscreen}) => {
	/* Auth state subscription */
	const unsubscribeOnAuthStateChanged = useRef(null);

	/* Get company id (if any) */
	const navigate = useNavigate();
	const params = useParams();
	const companyUrl = (params.companyUrl ? params.companyUrl.toLowerCase() : null);
	const companyData = companies.find((c) => {
		return (
			c.loginConfig && c.loginConfig.url === companyUrl
		);
	});


	/* Tenant ID (unique for each company) */
	const auth = firebase.auth();
	if (
		companyData && 
		companyData.loginConfig && 
		companyData.loginConfig.tenantId && 
		companyData.loginConfig.tenantId !== auth.tenantId
	) {
		auth.tenantId = companyData.loginConfig.tenantId;
	}
	
	/* Loading status */
	const [isLoading, setIsLoading] = useState(true);

	/* Language */
	const [languageId, setLanguageId] = useState(getBrowserLanguage());

	/* AuthFirebase data */
	const [authData, setAuthData] = useState(null);

	/* Auth error message */
	const [authErrMsg, setAuthErrMsg] = useState(null);


	/**
	 * Logout
	 */
	const handleLogout = () => {
		firebase.auth().signOut().then(() => {
			setAuthData(null);
		}).catch((error) => {
			console.error(error);
		});
	};
	
	/**
	 * Check if user is logged in 
	 */
	const checkIfLoggedIn = () => {
		/* Cancel previous subscription */
		if (unsubscribeOnAuthStateChanged.current !== null) {
			unsubscribeOnAuthStateChanged.current();
		}


		/* Subscribe to onAuthStateChanged */
		setAuthErrMsg(null);
		unsubscribeOnAuthStateChanged.current = firebase.auth().onAuthStateChanged((user)=>{
			if (user) {
				/* User is logged in */

				/* Get user email */
				let email = (user.email ? user.email : null);
				if (user.providerData && user.providerData.length > 0 && user.providerData[0].email) {
					email = user.providerData[0].email;
				}
				const isFacilitator = Boolean(email);

				/* Get user name */
				let name = (user.name ? user.name : null);
				if (user.displayName) {
					name = user.displayName;
				}
				if (user.providerData && user.providerData.length > 0 && user.providerData[0].displayName) {
					name = user.providerData[0].displayName;
				}
				
				const authData = {tenantId: user.tenantId, id: user.uid, email, name, isFacilitator};
				
				if (!isFacilitator) {
					/* Group login */
					setAuthData(authData);
					setIsLoading(false);
				} else {
					/* Facilitator login */
					if (
						(!companyData && !user.tenantId)  ||
						(
							companyData && 
							companyData.loginConfig && 
							companyData.loginConfig.loginMethod === 'openId' && 
							companyData.loginConfig.tenantId === user.tenantId
						)
					) {	
						/* No company data & no tentantId (valid default login) OR 
							company data & matching tenantId (valid open-id login) */
						if (
							companyData && 
							companyData.loginConfig && 
							companyData.loginConfig.loginMethod === 'openId'
						) {
							/* Valid open ID login */
							apiHelper('facilitator/check-openid-user', {
								userId: user.uid, 
								userEmail: email.toLowerCase(), 
								userName: name,
								companyId: companyData.id,
							}).then((response) => {
								if (response.status === 'success') {
									/* Success! Set auth data */
									setAuthData(authData);
									setIsLoading(false);
								} else {
									/* Error */
									setAuthData(null);
									setIsLoading(false);
									handleLogout();
									console.error(response.error);
									if (response.error === 'not-invited') {
										setAuthErrMsg(getText(loginUiTexts.userNotFacilitator, languageId));
									}
								}
							});
						} else {
							/* Valid default login */
							setAuthData(authData);
							setIsLoading(false);
						}
					} else {
						if (user.tenantId) {
							/* No or invalid company data, check if tenantId matches another company */
							const company = companies.find((c) => {
								return (
									c.loginConfig &&
									c.loginConfig.tenantId === user.tenantId
								);
							});
							if (company && company.loginConfig && company.loginConfig.url) {
								if (!companyData || company.id !== companyData) {
									/* Redirect to correct company */
									navigate('/' + company.loginConfig.url + '/' + gameType);	
									window.location.reload(true);
								}			
							} else {
								/* Invalid tenant id */
								setAuthData(null);
								setIsLoading(false);
								handleLogout();
								console.error('invalid tenant id: ', companyUrl, user.tenantId);
							}
						} else {
							/* No tenant id, company data, redirect to default login page */
							navigate('/' + gameType);
							window.location.reload(true);
							console.error('missing tenant id: ', companyUrl);
						}
					}
				}
			} else {
				/* Not logged in */
				setAuthData(null);
				setIsLoading(false);
			}
		});
	};



	/**
	 * Component did mount / will unmount
	 */
	useEffect(() => {
		/* Check if logged in */
		checkIfLoggedIn();
		
		return () => {
			/* Cancel subscriptions */
			if (unsubscribeOnAuthStateChanged.current) {
				unsubscribeOnAuthStateChanged.current();
			}
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	/* Loading */
	if (isLoading) {
		return <Loading />;
	}

	/* Not logged in, show login page */
	if (!authData) {
		return (
			<Login 
				languageId={languageId} 
				gameType={gameType} 
				authErrMsg={authErrMsg}
				companyData={companyData}
				updateLanguage={setLanguageId}
			/>
		);
	}

	/* User is logged in */
	const authProps = {
		userId: authData.id,
		isFacilitator: authData.isFacilitator,
		email: authData.email,
		name: authData.name,
		handleLogout: handleLogout
	};

	/* Get controller component for facilitator / group */
	const UserComponent = (authProps.isFacilitator ? FacilitatorController : GroupController);

	return (
		<DndProvider options={HTML5toTouch}>
			<UserComponent 
				{...authProps} 
				languageId={languageId}
				gameType={gameType} 
				isFullscreen={isFullscreen} 
				companyData={companyData}
				handleToggleFullscreen={handleToggleFullscreen}

			/>
		</DndProvider>
	);
};

Auth.propTypes = {
	isFullscreen: PropTypes.bool.isRequired,
	gameType: PropTypes.string.isRequired,
	companies: PropTypes.array.isRequired,
	handleToggleFullscreen: PropTypes.func.isRequired
};

export default Auth;
